import { ApolloError, type ApolloClient } from '@apollo/client';
import { ApplicationErrorCode } from '@oysterjs/core/graphql';
import { getApplicationError } from '@oysterjs/core/graphql/errors';
import type { LoaderFunction } from 'react-router-dom';
import { client } from '~/api/client';
import { queryData } from '~/api/data/request';
import { loadQuery } from '~/api/data/responses';
import { mapResult } from '~/api/data/result';
import {
  ClientFieldsFragment,
  ClientMergeSideDocument,
  ClientMergeSideQuery,
  ClientMergeSideQueryVariables,
  GetClientArtifactsDocument,
  GetClientArtifactsQuery,
  GetClientArtifactsQueryVariables,
  GetClientDocument,
  GetClientFilesDocument,
  GetClientFilesQuery,
  GetClientFilesQueryVariables,
  GetClientNotesDocument,
  GetClientNotesQuery,
  GetClientNotesQueryVariables,
  GetClientOverviewDocument,
  GetClientOverviewQuery,
  GetClientOverviewQueryVariables,
  GetClientQuery,
  GetClientQueryVariables,
  SearchCaraWorkspacesDocument,
  SearchCaraWorkspacesQuery,
  SearchCaraWorkspacesQueryVariables
} from '~/api/graphql';
import {
  clientActivityArtifactsVariables,
  clientActivityFilesVariables,
  clientActivityWorkspacesVariables,
  clientArtifactsVariables,
  clientFilesVariables,
  clientWorkspacesVariables
} from './variables';

export const getClient = (id: string) =>
  queryData<GetClientQuery, GetClientQueryVariables, ClientFieldsFragment | null>(
    client,
    { query: GetClientDocument, variables: { id } },
    (data) => data?.client ?? null
  );

// Merge decisions need fresh records, not the list's potentially stale snapshot.
export const getClientMergeSide = async (id: string, apollo: ApolloClient<object> = client) =>
  mapResult(
    await apollo.query<ClientMergeSideQuery, ClientMergeSideQueryVariables>({
      query: ClientMergeSideDocument,
      variables: { id },
      fetchPolicy: 'network-only'
    }),
    (data) => data?.client ?? null
  );

// Each tab warms exactly the first page its cache-first hook will read. A
// different filter, sort, or page size here would fetch that page twice.
export const getClientWorkspaces = (variables: SearchCaraWorkspacesQueryVariables) =>
  queryData<SearchCaraWorkspacesQuery, SearchCaraWorkspacesQueryVariables, boolean>(
    client,
    { query: SearchCaraWorkspacesDocument, variables },
    () => true
  );

export const getClientArtifacts = (variables: GetClientArtifactsQueryVariables) =>
  queryData<GetClientArtifactsQuery, GetClientArtifactsQueryVariables, boolean>(
    client,
    { query: GetClientArtifactsDocument, variables },
    () => true
  );

export const getClientFiles = (variables: GetClientFilesQueryVariables) =>
  queryData<GetClientFilesQuery, GetClientFilesQueryVariables, boolean>(
    client,
    { query: GetClientFilesDocument, variables },
    () => true
  );

export const getClientNotes = (clientId: string) =>
  queryData<GetClientNotesQuery, GetClientNotesQueryVariables, boolean>(
    client,
    { query: GetClientNotesDocument, variables: { clientId } },
    () => true
  );

export const getClientOverview = (id: string) =>
  queryData<GetClientOverviewQuery, GetClientOverviewQueryVariables, boolean>(
    client,
    { query: GetClientOverviewDocument, variables: { id } },
    () => true
  );

// Missing and cross-agency clients render the page's unavailable state instead
// of the route error page. The reusable query still retains the original error.
export const loadClient: LoaderFunction = async ({ params }) => {
  try {
    return await loadQuery(getClient(params.id || ''));
  } catch (error) {
    const code = getApplicationError(error instanceof ApolloError ? error : undefined)?.code;
    if (code === ApplicationErrorCode.NotFound || code === ApplicationErrorCode.Forbidden) {
      return null;
    }
    throw error;
  }
};

export const loadClientNotes: LoaderFunction = ({ params }) =>
  loadQuery(getClientNotes(params.id || ''));

export const loadClientOverview: LoaderFunction = ({ params }) =>
  loadQuery(getClientOverview(params.id || ''));

/** The newest page of each source the activity timeline interleaves. */
export const loadClientActivity: LoaderFunction = async ({ params }) => {
  const id = params.id || '';
  const results = await Promise.all([
    loadQuery(getClientWorkspaces(clientActivityWorkspacesVariables(id))),
    loadQuery(getClientArtifacts(clientActivityArtifactsVariables(id))),
    loadQuery(getClientFiles(clientActivityFilesVariables(id))),
    loadQuery(getClientNotes(id))
  ]);
  return results.find((result) => result instanceof Response) ?? true;
};

export const loadClientWorkspaces: LoaderFunction = ({ params }) =>
  loadQuery(getClientWorkspaces(clientWorkspacesVariables(params.id || '')));

export const loadClientArtifacts: LoaderFunction = ({ params }) =>
  loadQuery(getClientArtifacts(clientArtifactsVariables(params.id || '')));

export const loadClientFiles: LoaderFunction = ({ params }) =>
  loadQuery(getClientFiles(clientFilesVariables(params.id || '')));
